import { useTranslate } from "@tolgee/react";
import type { WithdrawalRow, WithdrawalStatus } from "#/lib/api/referrals";
import { formatDateTime, formatUsd } from "#/lib/formatters";
import { ARBISCAN_TX_BASE } from "../lib/config";
import { useMyWithdrawals } from "../hooks/use-my-withdrawals";

/**
 * Renders the caller's recent withdrawals (capped server-side at 50).
 * Polls every 10s while any row is pending/sent so users see the
 * pending→sent→confirmed transitions live. See use-my-withdrawals for
 * the polling logic.
 */
export function WithdrawalsHistory() {
	const { t } = useTranslate();
	const withdrawals = useMyWithdrawals();
	const rows = withdrawals.data?.withdrawals ?? [];

	if (rows.length === 0) {
		return null;
	}

	return (
		<section>
			<div className="mb-2">
				<h2 className="text-headline text-foreground">
					{t("referrals.withdrawals.title")}
				</h2>
			</div>
			<div className="overflow-x-auto">
				<table className="w-full text-body2">
					<thead className="text-label2-medium text-muted-foreground">
						<tr className="border-b border-border">
							<th className="px-5 py-3 text-left">
								{t("referrals.withdrawals.columns.amount")}
							</th>
							<th className="px-5 py-3 text-left">
								{t("referrals.withdrawals.columns.status")}
							</th>
							<th className="px-5 py-3 text-left">
								{t("referrals.withdrawals.columns.date")}
							</th>
							<th className="px-5 py-3 text-right">
								{t("referrals.withdrawals.columns.tx")}
							</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => (
							<Row key={row.id} row={row} />
						))}
					</tbody>
				</table>
			</div>
		</section>
	);
}

function Row({ row }: { row: WithdrawalRow }) {
	const { t } = useTranslate();
	return (
		<tr className="border-b border-border last:border-b-0">
			<td className="px-5 py-3 text-label1-tabular text-foreground">
				{formatUsd(row.amount_usd)}
			</td>
			<td className="px-5 py-3">
				<StatusPill status={row.status} />
			</td>
			<td className="px-5 py-3 text-muted-foreground">
				{formatDateTime(Date.parse(row.created_at))}
			</td>
			<td className="px-5 py-3 text-right">
				{row.tx_hash ? (
					<a
						href={`${ARBISCAN_TX_BASE}${row.tx_hash}`}
						target="_blank"
						rel="noreferrer noopener"
						className="text-label2 text-primary underline-offset-2 hover:underline"
					>
						{t("referrals.withdrawals.view-on-arbiscan")}
					</a>
				) : (
					<span className="text-label2 text-muted-foreground">—</span>
				)}
			</td>
		</tr>
	);
}

// Pill colors map to the row's lifecycle stage. Failed rows are red so
// users can spot them at a glance; sent is amber (in flight), confirmed
// is green (terminal success), pending is neutral.
const STATUS_STYLES: Record<WithdrawalStatus, string> = {
	pending: "bg-muted text-muted-foreground",
	sent: "bg-[var(--color-theme-denotives-yellow)]/15 text-[var(--color-theme-denotives-yellow)] dark:text-[var(--color-theme-denotives-yellow)]",
	confirmed: "bg-[var(--color-theme-status-positive)]/15 text-[var(--color-theme-status-positive)] dark:text-[var(--color-theme-status-positive)]",
	failed: "bg-destructive/15 text-destructive",
};

function StatusPill({ status }: { status: WithdrawalStatus }) {
	const { t } = useTranslate();
	return (
		<span
			className={`inline-flex items-center rounded-full px-2 py-0.5 text-label2-medium ${STATUS_STYLES[status]}`}
		>
			{t(`referrals.withdrawals.status.${status}`)}
		</span>
	);
}
